import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PageHeader } from 'client/js/util/layout_utils';
import AceEditor from 'react-ace';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { fetchJSON } from 'client/js/util/rest_utils';

// Import ace modes and themes
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/mode-php';
import 'ace-builds/src-noconflict/mode-ruby';
import 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';

const DEFAULT_BASE_URL = '/usercp/filemanager/api';

const getModeFromExtension = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return 'javascript';
    case 'json':
      return 'json';
    case 'html':
    case 'erb':
      return 'html';
    case 'css':
    case 'scss':
    case 'sass':
      return 'css';
    case 'php':
      return 'php';
    case 'rb':
      return 'ruby';
    default:
      return 'text';
  }
};

const FileEditor = ({ file, onClose, baseApiUrl = DEFAULT_BASE_URL }) => {
  const [content, setContent] = useState('');
  const [theme, setTheme] = useState('github');
  const queryClient = useQueryClient();

  // Helper function to encode path for URL
  const encodePath = (path) => {
    return btoa(path).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  // Fetch file content
  const { data, isLoading, error } = useQuery({
    queryKey: ['filemanager', 'content', file.path],
    queryFn: async () => {
      const encodedPath = encodePath(file.path);
      return fetchJSON(`${baseApiUrl}/${encodedPath}`, {
        method: 'GET',
      });
    },
    staleTime: 0, // Always fetch fresh content
  });

  useEffect(() => {
    if (data && data.content !== undefined) {
      setContent(data.content);
    }
  }, [data]);

  const saveFileMutation = useMutation({
    mutationFn: async (newContent) => {
      // We use createItem endpoint (POST to root) with same path to overwrite file content
      // Or check if there is an update endpoint for content.
      // The createItem in `use_filemanager.js` does `fetchJSON(baseApiUrl, { method: 'POST', ... })`
      // The backend `create` action seems to handle overwrite if file exists?
      // Let's check backend implementation. `create_file` raises error if file exists.
      // We probably need to delete and recreate or add an update content endpoint.
      // Wait, the backend `create` method in `LocalFilemanagerBackend` raises "File already exists".
      // But `File.write(new_file, content)` in `create_file` would overwrite if we bypassed the check.
      // The controller `create` action calls `@backend.create_file`.
      //
      // Maybe we should use a PUT request to update content?
      // The `update` action in controller handles 'rename' and 'chmod'.
      // We might need to add a new action type 'update_content' to `update` action or a separate endpoint.
      //
      // Given I cannot easily change backend logic without potentially breaking things or needing review,
      // and I skipped backend zip part, I should check if I can modify backend for this or use existing.
      //
      // Let's look at `app/controllers/usercp/filemanager_controller.rb` again.
      // `create` uses `create_file`.
      // `update` uses `rename` or `chmod`.
      //
      // I should probably add a way to update file content.
      // Since I am the dev, I can add `write_file` or similar to backend and controller.
      //
      // Let's add 'update_content' to `update` action in controller and backend.

      const encodedPath = encodePath(file.path);
      return fetchJSON(`${baseApiUrl}/${encodedPath}`, {
        method: 'PATCH',
        body: JSON.stringify({ action_type: 'update_content', content: newContent }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filemanager', 'content', file.path] });
      // Also invalidate list if size changed (though less critical)
      const parentPath = file.path.substring(0, file.path.lastIndexOf('/')) || '/';
      queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', parentPath, baseApiUrl] });
      toast.success('Datei erfolgreich gespeichert');
      onClose();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSave = () => {
    saveFileMutation.mutate(content);
  };

  const mode = getModeFromExtension(file.name);

  const headerButtons = (
    <div>
      <button type="button" className="btn btn-secondary mr-2" onClick={onClose}>
        Abbrechen
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saveFileMutation.isLoading || isLoading}
      >
        {saveFileMutation.isLoading ? 'Speichere...' : 'Speichern'}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <>
        <PageHeader text="Lade Datei..." buttons={headerButtons} />
        <div className="text-center p-5">
          <div className="spinner-wrap"><div className="css-spinloader"></div></div>
          <p className="mt-3">Lade Datei...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader text="Fehler" buttons={headerButtons} />
        <div className="alert alert-danger m-3">
          <h5>Fehler beim Laden der Datei</h5>
          <p>{error.message}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader text={file.name} buttons={headerButtons} />

      <div className="mb-3 mx-3">
        <div className="form-inline">
          <label className="mr-2">Theme:</label>
          <select
            className="form-control form-control-sm"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
          >
            <option value="github">Light (GitHub)</option>
            <option value="monokai">Dark (Monokai)</option>
          </select>
          <span className="ml-3 text-muted">Syntax: {mode}</span>
        </div>
      </div>

      <div style={{ height: 'calc(100vh - 250px)', position: 'relative' }}>
        <AceEditor
          mode={mode}
          theme={theme}
          onChange={setContent}
          value={content}
          name="file-editor"
          editorProps={{ $blockScrolling: true }}
          width="100%"
          height="100%"
          fontSize={14}
          showPrintMargin={false}
          showGutter={true}
          highlightActiveLine={true}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: false,
            enableSnippets: false,
            showLineNumbers: true,
            tabSize: 2,
          }}
        />
      </div>
    </>
  );
};

FileEditor.propTypes = {
  file: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  baseApiUrl: PropTypes.string,
};

export default FileEditor;
